import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../Config";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Table,
  Tag,
  Alert,
  Popconfirm,
  Space,
  Typography,
  Divider,
  message,
} from "antd";
import { StopOutlined, UnlockOutlined } from "@ant-design/icons";

const { Text } = Typography;

const PENALTY_TAGS = {
  warning: { color: "orange", label: "Warning" },
  "ban-7-days": { color: "volcano", label: "7-day ban" },
  "ban-30-days": { color: "red", label: "30-day ban" },
  permanent: { color: "magenta", label: "Permanent ban" },
};

const DIRECT_BAN_OPTIONS = [
  { label: "Copyright infringement", value: "copyright" },
  { label: "Obscene content", value: "obscene" },
  { label: "AI-generated image", value: "ai-generated" },
];

// What the next (non-direct) offence will do — mirrors the backend escalation
const nextPenalty = (count) =>
  count === 0
    ? "warning"
    : count === 1
      ? "ban-7-days"
      : count === 2
        ? "ban-30-days"
        : "permanent";

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token")}`,
});

const UserViolationsModal = ({ open, user, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [info, setInfo] = useState(null);
  const [variations, setVariations] = useState([]);
  const directBan = Form.useWatch("directBan", form);

  const load = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [violRes, userRes] = await Promise.all([
        axios.get(`${BASE_URL}/users/${user.id}/violations`, { headers: authHeaders() }),
        axios.get(`${BASE_URL}/users/${user.id}`, { headers: authHeaders() }),
      ]);
      setInfo(violRes.data.data);
      setVariations(userRes.data.data?.variations || []);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to load violations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      setInfo(null);
      setVariations([]);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${BASE_URL}/users/${user.id}/violations`,
        {
          reason: values.reason.trim(),
          variationId: values.variationId || undefined,
          directBan: values.directBan || undefined,
        },
        { headers: authHeaders() },
      );
      const penalty = PENALTY_TAGS[res.data.data?.penalty]?.label || "Violation";
      message.success(`${penalty} recorded (offence #${res.data.data?.offence})`);
      form.resetFields();
      load();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to record violation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLiftBan = async () => {
    try {
      setLifting(true);
      await axios.delete(`${BASE_URL}/users/${user.id}/ban`, { headers: authHeaders() });
      message.success("Ban lifted");
      load();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to lift ban");
    } finally {
      setLifting(false);
    }
  };

  const count = info?.violationCount ?? 0;
  const activeTempBan = info?.bannedUntil && new Date(info.bannedUntil) > new Date();
  const isBanned = info?.permanentlyBanned || activeTempBan;
  const upcoming = directBan ? "permanent" : nextPenalty(count);

  return (
    <Modal
      open={open}
      title={`Violations — ${user?.name || ""}`}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnHidden
    >
      {info && (
        <Alert
          style={{ marginBottom: 16 }}
          type={isBanned ? "error" : count > 0 ? "warning" : "success"}
          showIcon
          message={
            info.permanentlyBanned
              ? "Permanently banned"
              : activeTempBan
                ? `Banned until ${new Date(info.bannedUntil).toLocaleString()}`
                : "Not banned"
          }
          description={`${count} violation${count === 1 ? "" : "s"} on record. The offence count is kept when a ban is lifted.`}
          action={
            isBanned && (
              <Popconfirm
                title="Lift this user's ban?"
                description="They will be able to sign in and upload again."
                onConfirm={handleLiftBan}
                okText="Lift ban"
              >
                <Button size="small" icon={<UnlockOutlined />} loading={lifting}>
                  Lift ban
                </Button>
              </Popconfirm>
            )
          }
        />
      )}

      <Text strong>Record violation</Text>
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 8 }}>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, whitespace: true, message: "A reason is required" },
            { min: 3, message: "At least 3 characters" },
          ]}
        >
          <Input.TextArea rows={2} placeholder="Which rule was broken?" />
        </Form.Item>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Form.Item
            name="variationId"
            label="Variation to remove (optional)"
            style={{ flex: 1, minWidth: 260 }}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="None"
              options={variations.map((v) => ({
                value: v.variationId,
                label: `${v.english || "Untitled"}${v.sanskrit ? ` (${v.sanskrit})` : ""}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="directBan"
            label="Direct ban (optional)"
            style={{ flex: 1, minWidth: 220 }}
          >
            <Select allowClear placeholder="No — normal escalation" options={DIRECT_BAN_OPTIONS} />
          </Form.Item>
        </div>
        <Space style={{ justifyContent: "space-between", width: "100%" }} wrap>
          <Text type="secondary">
            This will apply: <Tag color={PENALTY_TAGS[upcoming].color}>{PENALTY_TAGS[upcoming].label}</Tag>
          </Text>
          <Popconfirm
            title={`Apply ${PENALTY_TAGS[upcoming].label.toLowerCase()}?`}
            description="The selected variation is deleted and the user is notified."
            onConfirm={() => form.submit()}
            okText="Record"
            okButtonProps={{ danger: true }}
          >
            <Button danger type="primary" icon={<StopOutlined />} loading={submitting}>
              Record violation
            </Button>
          </Popconfirm>
        </Space>
      </Form>

      <Divider />
      <Text strong>History</Text>
      <Table
        style={{ marginTop: 8 }}
        size="small"
        loading={loading}
        dataSource={[...(info?.violations || [])].reverse()}
        rowKey={(r) => r._id || r.at}
        pagination={false}
        locale={{ emptyText: "No violations" }}
        columns={[
          {
            title: "Date",
            dataIndex: "at",
            render: (d) => (d ? new Date(d).toLocaleString() : "-"),
          },
          { title: "Reason", dataIndex: "reason" },
          {
            title: "Penalty",
            render: (_, r) => (
              <Space size={4}>
                <Tag color={PENALTY_TAGS[r.penalty]?.color}>
                  {PENALTY_TAGS[r.penalty]?.label || r.penalty}
                </Tag>
                {r.direct && <Tag>Direct</Tag>}
              </Space>
            ),
          },
          {
            title: "Variation removed",
            dataIndex: "variationId",
            render: (id) => (id ? <Text type="secondary" title={id}>Yes</Text> : "-"),
          },
        ]}
      />
    </Modal>
  );
};

export default UserViolationsModal;
